import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import Toast from '../../components/ui/Toast';
import { knowledgeHubService } from '../../services/knowledgeHubService';
import { format } from 'date-fns';

const KnowledgeHubAdmin = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [content, setContent] = useState([]);
  const [filteredContent, setFilteredContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [toast, setToast] = useState(null);
  const [filters, setFilters] = useState({
    contentType: 'all',
    status: 'all',
    searchTerm: ''
  });

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    content_type: 'article',
    status: 'draft',
    author: 'Northstar Analytics',
    author_role: 'Data Strategy Experts',
    author_image: '',
    category: '',
    tags: [],
    image_url: '',
    video_url: '',
    read_time: '',
    publish_date: new Date()?.toISOString()?.split('T')?.[0],
    is_featured: false
  });

  useEffect(() => {
    if (!user) {
      navigate('/signin');
      return;
    }
    
    if (userProfile?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }

    loadContent();
  }, [user, userProfile, navigate]);

  useEffect(() => {
    applyFilters();
  }, [content, filters]);

  const loadContent = async () => {
    try {
      setLoading(true);
      const data = await knowledgeHubService?.getAllContent();
      setContent(data || []);
    } catch (err) {
      showToast(err?.message || 'Failed to load content', 'error');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...content];

    if (filters?.contentType !== 'all') {
      filtered = filtered?.filter(item => item?.content_type === filters?.contentType);
    }

    if (filters?.status !== 'all') {
      filtered = filtered?.filter(item => item?.status === filters?.status);
    }

    if (filters?.searchTerm) {
      const term = filters?.searchTerm?.toLowerCase();
      filtered = filtered?.filter(item => 
        item?.title?.toLowerCase()?.includes(term) ||
        item?.excerpt?.toLowerCase()?.includes(term) ||
        item?.category?.toLowerCase()?.includes(term)
      );
    }

    setFilteredContent(filtered);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Auto-generate slug from title
    if (name === 'title' && !editingItem) {
      setFormData(prev => ({
        ...prev,
        slug: knowledgeHubService?.generateSlug(value)
      }));
    }
  };

  const handleTagsChange = (e) => {
    const tagsString = e?.target?.value;
    const tagsArray = tagsString?.split(',')?.map(tag => tag?.trim())?.filter(tag => tag);
    setFormData(prev => ({ ...prev, tags: tagsArray }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    try {
      setLoading(true);
      
      if (editingItem) {
        await knowledgeHubService?.updateContent(editingItem?.id, formData);
        showToast('Content updated successfully', 'success');
      } else {
        await knowledgeHubService?.createContent(formData);
        showToast('Content created successfully', 'success');
      }
      
      setShowForm(false);
      setEditingItem(null);
      resetForm();
      await loadContent();
    } catch (err) {
      showToast(err?.message || 'Failed to save content', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item?.title || '',
      slug: item?.slug || '',
      excerpt: item?.excerpt || '',
      content: item?.content || '',
      content_type: item?.content_type || 'article',
      status: item?.status || 'draft',
      author: item?.author || 'Northstar Analytics',
      author_role: item?.author_role || 'Data Strategy Experts',
      author_image: item?.author_image || '',
      category: item?.category || '',
      tags: item?.tags || [],
      image_url: item?.image_url || '',
      video_url: item?.video_url || '',
      read_time: item?.read_time || '',
      publish_date: item?.publish_date ? new Date(item?.publish_date)?.toISOString()?.split('T')?.[0] : new Date()?.toISOString()?.split('T')?.[0],
      is_featured: item?.is_featured || false
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this content?')) return;
    
    try {
      setLoading(true);
      await knowledgeHubService?.deleteContent(id);
      showToast('Content deleted successfully', 'success');
      await loadContent();
    } catch (err) {
      showToast(err?.message || 'Failed to delete content', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems?.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedItems?.length} items?`)) return;
    
    try {
      setLoading(true);
      await knowledgeHubService?.bulkDeleteContent(selectedItems);
      showToast(`${selectedItems?.length} items deleted successfully`, 'success');
      setSelectedItems([]);
      await loadContent();
    } catch (err) {
      showToast(err?.message || 'Failed to delete items', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectItem = (id) => {
    setSelectedItems(prev => 
      prev?.includes(id) ? prev?.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedItems?.length === filteredContent?.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredContent?.map(item => item?.id));
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      content_type: 'article',
      status: 'draft',
      author: 'Northstar Analytics',
      author_role: 'Data Strategy Experts',
      author_image: '',
      category: '',
      tags: [],
      image_url: '',
      video_url: '',
      read_time: '',
      publish_date: new Date()?.toISOString()?.split('T')?.[0],
      is_featured: false
    });
  };

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
  };

  const getStatusColor = (status) => {
    const colors = {
      'draft': 'bg-gray-100 text-gray-800 border-gray-200',
      'published': 'bg-[#D1F5F0] text-[#1FB5A5] border-[#1FB5A5]/30',
      'archived': 'bg-yellow-100 text-yellow-800 border-yellow-200'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getContentTypeIcon = (type) => {
    const icons = {
      'article': 'FileText',
      'video': 'Video',
      'newsletter': 'Mail'
    };
    return icons?.[type] || 'FileText';
  };

  if (loading && content?.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-24 flex items-center justify-center">
          <div className="text-center">
            <Icon name="Loader" size={48} className="text-primary animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading content...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Knowledge Hub Admin | Northstar Analytics</title>
      </Helmet>
      <Header />
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-primary mb-2">Knowledge Hub Admin</h1>
                <p className="text-muted-foreground">Manage articles, videos, and newsletters</p>
              </div>
              <div className="flex items-center space-x-3">
                <Button
                  onClick={() => navigate('/knowledge-hub-admin/create-article')}
                  iconName="PenLine"
                  iconPosition="left"
                >
                  New Article
                </Button>
                <Button
                  onClick={() => {
                    setShowForm(true);
                    setEditingItem(null);
                    resetForm();
                  }}
                  variant="outline"
                  iconName="Plus"
                  iconPosition="left"
                >
                  Quick Add
                </Button>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl shadow-elevation-1 p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Input
                  type="text"
                  placeholder="Search content..."
                  value={filters?.searchTerm}
                  onChange={(e) => setFilters(prev => ({ ...prev, searchTerm: e?.target?.value }))}
                  iconName="Search"
                />
                <Select
                  value={filters?.contentType}
                  onChange={(e) => setFilters(prev => ({ ...prev, contentType: e?.target?.value }))}
                >
                  <option value="all">All Types</option>
                  <option value="article">Articles</option>
                  <option value="video">Videos</option>
                  <option value="newsletter">Newsletters</option>
                </Select>
                <Select
                  value={filters?.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e?.target?.value }))}
                >
                  <option value="all">All Status</option>
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </Select>
                {selectedItems?.length > 0 && (
                  <Button
                    onClick={handleBulkDelete}
                    variant="outline"
                    iconName="Trash2"
                    iconPosition="left"
                  >
                    Delete ({selectedItems?.length})
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Content Form */}
          {showForm && (
            <div className="bg-white rounded-xl shadow-elevation-1 p-6 mb-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-primary">
                  {editingItem ? 'Edit Content' : 'Add New Content'}
                </h2>
                <Button
                  onClick={() => {
                    setShowForm(false);
                    setEditingItem(null);
                    resetForm();
                  }}
                  variant="ghost"
                  iconName="X"
                />
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-foreground mb-2">Title *</label>
                    <Input
                      type="text"
                      name="title"
                      value={formData?.title}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter content title"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Slug *</label>
                    <Input
                      type="text"
                      name="slug"
                      value={formData?.slug}
                      onChange={handleInputChange}
                      required
                      placeholder="url-friendly-slug"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Content Type *</label>
                    <Select
                      name="content_type"
                      value={formData?.content_type}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="article">Article</option>
                      <option value="video">Video</option>
                      <option value="newsletter">Newsletter</option>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Status *</label>
                    <Select
                      name="status"
                      value={formData?.status}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="archived">Archived</option>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Category</label>
                    <Input
                      type="text"
                      name="category"
                      value={formData?.category}
                      onChange={handleInputChange}
                      placeholder="e.g., Data Governance"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-foreground mb-2">Excerpt</label>
                    <textarea
                      name="excerpt"
                      value={formData?.excerpt}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Brief description of the content"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-foreground mb-2">Content *</label>
                    <textarea
                      name="content"
                      value={formData?.content}
                      onChange={handleInputChange}
                      rows={10}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm"
                      placeholder="Full content (supports markdown)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Author</label>
                    <Input
                      type="text"
                      name="author"
                      value={formData?.author}
                      onChange={handleInputChange}
                      placeholder="Author name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Author Role</label>
                    <Input
                      type="text"
                      name="author_role"
                      value={formData?.author_role}
                      onChange={handleInputChange}
                      placeholder="e.g., Data Strategy Expert"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Image URL</label>
                    <Input
                      type="url"
                      name="image_url"
                      value={formData?.image_url}
                      onChange={handleInputChange}
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>

                  {formData?.content_type === 'video' && (
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Video URL</label>
                      <Input
                        type="url"
                        name="video_url"
                        value={formData?.video_url}
                        onChange={handleInputChange}
                        placeholder="https://youtube.com/watch?v=..."
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Read Time</label>
                    <Input
                      type="text"
                      name="read_time"
                      value={formData?.read_time}
                      onChange={handleInputChange}
                      placeholder="e.g., 10 min read"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Publish Date</label>
                    <Input
                      type="date"
                      name="publish_date"
                      value={formData?.publish_date}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-foreground mb-2">Tags (comma-separated)</label>
                    <Input
                      type="text"
                      value={formData?.tags?.join(', ')}
                      onChange={handleTagsChange}
                      placeholder="e.g., Governance, Best Practices, Framework"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="is_featured"
                        checked={formData?.is_featured}
                        onChange={handleInputChange}
                        className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="text-sm font-medium text-foreground">Featured Content</span>
                    </label>
                  </div>
                </div>

                <div className="flex items-center justify-end space-x-4 pt-6 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowForm(false);
                      setEditingItem(null);
                      resetForm();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading}>
                    {loading ? 'Saving...' : editingItem ? 'Update Content' : 'Create Content'}
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Content List */}
          <div className="bg-white rounded-xl shadow-elevation-1 overflow-hidden">
            {filteredContent?.length === 0 ? (
              <div className="p-12 text-center">
                <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No content found</h3>
                <p className="text-muted-foreground mb-6">Get started by creating your first content.</p>
                <Button onClick={() => setShowForm(true)} iconName="Plus" iconPosition="left">
                  Add Content
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-left">
                        <input
                          type="checkbox"
                          checked={selectedItems?.length === filteredContent?.length && filteredContent?.length > 0}
                          onChange={handleSelectAll}
                          className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                        />
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Content
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Published
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredContent?.map((item) => (
                      <tr key={item?.id} className="hover:bg-gray-50 transition-colors duration-150">
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={selectedItems?.includes(item?.id)}
                            onChange={() => handleSelectItem(item?.id)}
                            className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-start space-x-3">
                            {item?.image_url && (
                              <img
                                src={item?.image_url}
                                alt={item?.title}
                                className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">{item?.title}</p>
                              <p className="text-xs text-muted-foreground truncate">{item?.excerpt}</p>
                              {item?.category && (
                                <span className="inline-block mt-1 text-xs text-secondary">{item?.category}</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <Icon name={getContentTypeIcon(item?.content_type)} size={16} className="text-muted-foreground" />
                            <span className="text-sm text-foreground capitalize">{item?.content_type}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(item?.status)}`}>
                            {item?.status?.charAt(0)?.toUpperCase() + item?.status?.slice(1)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-foreground">
                            {item?.publish_date ? format(new Date(item?.publish_date), 'MMM dd, yyyy') : '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end space-x-2">
                            <Button
                              onClick={() => navigate(`/knowledge-hub-admin/edit-article/${item?.id}`)}
                              variant="ghost"
                              size="sm"
                              iconName="Edit2"
                            />
                            <Button
                              onClick={() => handleDelete(item?.id)}
                              variant="ghost"
                              size="sm"
                              iconName="Trash2"
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
      {toast && (
        <Toast
          message={toast?.message}
          type={toast?.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default KnowledgeHubAdmin;